import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import TopNavigation from "../../components/global_components/TopNavigation";
import identitySelectors from "../../selectors/identity";
import Page403 from "../../components/global_components/Page403";
import useCrmListFieldDefinitions from "../hooks/useCrmListFieldDefinitions";
import { parseAsArrayOf, parseAsString } from "nuqs";
import { nuqsDataFilterParser } from "../utils/nuqs/nuqsDataFilterParser";
import { useFlyout } from "../../components/global_components/Flyout";
import { FilterFlyout } from "./filters/FilterFlyout";
import { Button, Checkbox, Dropdown, Icon, Input, Loader, Popup, Segment } from "semantic-ui-react";
import { faDownload, faExclamationTriangle, faFilter, faTableColumns } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlusButton } from "../../components/PlusButton";
import { useInfiniteCrmRequests } from "../hooks/useInfiniteCrmRequests";
import { Column as ReactTableColumn, useColumnOrder, useSortBy, useTable } from "react-table";
import { AutoSizer, Column, InfiniteLoader, Table } from "react-virtualized";
import { DateBadge, DateDueBadge, DateTimeBadge } from "../../components/badges";
import AppSettings from "../../config";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import extractErrorInfo from "../../utils/extractErrorInfo";
import FormHeader from "../../components/global_components/form_components/FormHeader";
import { useMedia } from "react-media";
import useCrmLookUpOptions from "../hooks/useCrmLookUpOptions";
import { useFeatureFlag } from "../../hooks/use-feature-flag";
import { useClient } from "../../hooks/use-client";
import useUserPreference from "../../hooks/use-user-preference";
import { useDebouncedCallback } from "../../hooks/useDebouncedCallback";
import { useQueryStateWithLocalStorage } from "../../hooks/useQueryStateWithLocalStorage";
import useCrmCustomListViews from "../hooks/useCrmCustomListViews";
import { CrmCustomListView } from "../queries/getCrmCustomListViews";
import { SaveNewCrmListViewModal } from "./views/SaveNewCrmListViewModal";
import { RemoveViewModal } from "../../features/permits/components/RemoveViewModal";
import { nuqsSortFieldParser } from "../utils/nuqs/nuqsSortFieldParser";
import { stringEqualityCheck, orderedObjectArrayEqualityCheck } from "../utils/equalityChecks";
import { useCrmSearchExport } from "../hooks/useCrmSearchExport";
import { ColumnFlyout } from "./columns/ColumnFlyout";
import { nuqsColumnFieldParser } from "../utils/nuqs/nuqsColumnFieldParser";
import { ListViewDesktopColumns } from "./columns/ListViewDesktopColumns";

export const CrmDemoList = () => {
  const isInternalAdmin = useSelector(identitySelectors.isInternalUser);
  if (!isInternalAdmin) {
    return <Page403></Page403>;
  }
  const { site } = useParams<{
    site: string;
  }>();
  const isSmallScreen = useMedia({ query: "(max-width: 1100px)" });
  const rowHeight = isSmallScreen ? 110 : 90;

  const featureNewLookForce = useFeatureFlag("crm.new_look_force");

  const listModeCrmPreference = useUserPreference("crm.list.mode");
  const listModeCePreference = useUserPreference("ce.list.mode");
  const landingPreference = useUserPreference("landing.home");

  const isSuperAdmin = useSelector(identitySelectors.isSuperAdmin);
  const isDepartmentAdmin = useSelector(identitySelectors.isDepartmentAdmin);
  const isCrmAdmin = useSelector(identitySelectors.isCrmAdmin);

  //for controlling the options of access level for view creation and editing
  const fullAccess = isSuperAdmin || isCrmAdmin || isInternalAdmin;
  const departmentalAccess = isDepartmentAdmin;

  const { clientId } = useClient(site);
  const canReturnToClassic = !featureNewLookForce.isEnabled && (site.startsWith("test") || clientId < 230);

  const pageWhenDone = encodeURIComponent(encodeURIComponent(window.location.href));
  const legacyUrl = `${AppSettings.GOGOV_WEB_URL}/${site}/editcase.php?cmd=new&pagewhendone=${pageWhenDone}`; //TODO: add mobile url

  const { isOpen: isFilterSideBarOpen, close: closeFilterSidebar, open: openFilterSideBar } = useFlyout();
  const { isOpen: isColumnSideBarOpen, close: closeColumnSidebar, open: openColumnSideBar } = useFlyout();

  const [selectedCases, setSelectedCases] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const {
    isCrmCustomListViewsLoading,
    crmCustomListViews,
    createCustomListViewMutation,
    isCreateCustomListViewMutationLoading,
    updateCustomListViewMutation,
    isUpdateCustomListViewMutationLoading,
    deleteCustomListViewMutation,
    isDeleteCustomListViewMutationLoading,
  } = useCrmCustomListViews();
  const { isCrmListFieldDefinitionsLoading, crmListFieldDefinitions } = useCrmListFieldDefinitions();
  const { crmSearchExportMutation, isCrmSearchExportLoading } = useCrmSearchExport();

  const isLoading =
    isCrmCustomListViewsLoading ||
    isCrmListFieldDefinitionsLoading ||
    isCreateCustomListViewMutationLoading ||
    isUpdateCustomListViewMutationLoading ||
    isDeleteCustomListViewMutationLoading;

  const [viewId, setViewId] = useQueryStateWithLocalStorage(
    "viewId",
    `crm-list-viewId-${site}`,
    parseAsString
      .withOptions({
        clearOnDefault: false,
      })
      .withDefault("0"),
  );

  const currentView = useMemo(() => {
    // Find the current view based on the viewId from the query string
    return crmCustomListViews?.find((v) => String(v.id) === viewId);
  }, [viewId, crmCustomListViews, isCrmCustomListViewsLoading]);

  const [advancedLogic, setAdvancedLogic] = useQueryStateWithLocalStorage(
    "advancedLogic",
    `crm-list-advancedLogic-${site}`,
    parseAsString
      .withOptions({
        clearOnDefault: isCrmCustomListViewsLoading,
      })
      .withDefault(currentView?.filterGroup?.advancedLogic || ""),
    stringEqualityCheck,
  );
  const [filters, setFilters] = useQueryStateWithLocalStorage(
    "filters",
    `crm-list-filters-${site}`,
    parseAsArrayOf(nuqsDataFilterParser)
      .withOptions({
        clearOnDefault: isCrmCustomListViewsLoading,
      })
      .withDefault(currentView?.filterGroup?.filters || []),
    orderedObjectArrayEqualityCheck,
  );
  const [sortBy, setSortBy] = useQueryStateWithLocalStorage(
    "sort",
    `crm-list-sort-${site}`,
    parseAsArrayOf(nuqsSortFieldParser)
      .withOptions({
        clearOnDefault: isCrmCustomListViewsLoading,
      })
      .withDefault(currentView?.sortFields || []),
    orderedObjectArrayEqualityCheck,
  );
  const [columns, setColumns] = useQueryStateWithLocalStorage(
    "columns",
    `crm-list-columns-${site}`,
    parseAsArrayOf(nuqsColumnFieldParser)
      .withOptions({
        clearOnDefault: isCrmCustomListViewsLoading,
      })
      .withDefault(currentView?.columns || []),
    orderedObjectArrayEqualityCheck,
  );
  const [query, setQuery] = useQueryStateWithLocalStorage(
    "query",
    `crm-list-query-${site}`,
    parseAsString
      .withOptions({
        clearOnDefault: isCrmCustomListViewsLoading,
      })
      .withDefault(currentView?.query || ""),
    stringEqualityCheck,
  );

  const [debouncedQuery, setDebouncedQuery] = useState<string>(query);

  const viewChanged = useMemo(() => {
    return (
      currentView &&
      (!stringEqualityCheck(currentView.filterGroup?.advancedLogic, advancedLogic) ||
        !orderedObjectArrayEqualityCheck(currentView.filterGroup?.filters, filters) ||
        !orderedObjectArrayEqualityCheck(currentView.sortFields, sortBy) ||
        !orderedObjectArrayEqualityCheck(currentView.columns, columns) ||
        !stringEqualityCheck(currentView.query, query))
    );
  }, [filters, advancedLogic, sortBy, query, currentView, columns]);

  const setCurrentView = (id: string) => {
    const currentView = crmCustomListViews?.find((view) => String(view.id) === id);
    if (!currentView) return; //shouldn't happen but just in case
    setViewId(id);
    updateAllQueryParams(currentView);
  };

  const updateAllQueryParams = (view: CrmCustomListView) => {
    setAdvancedLogic(view.filterGroup?.advancedLogic || "");
    setFilters(view.filterGroup?.filters || []);
    setSortBy(view.sortFields || []);
    setColumns(view.columns || []);
    setQuery(view.query || "");
    setDebouncedQuery(view.query || "");
    scrollToTop();
  };

  const createView = async (data: CrmCustomListView) => {
    const response = await createCustomListViewMutation(data);
    if (response) {
      //use id since the current view is not updated yet in the current render loop
      setViewId(String(response.id));
    }
  };

  const updateView = async (data: CrmCustomListView, id: number) => {
    const response = await updateCustomListViewMutation({
      body: data,
      id,
    });
    if (response) {
      //use id since the current view is not updated yet in the current render loop
      setViewId(String(response.id));
    }
  };

  const [isNewView, setIsNewView] = useState(false);

  const [isOpenSaveNewViewModalOpen, setIsOpenSaveNewViewModalOpen] = useState(false);
  const [isDeleteViewModalOpen, setIsDeleteViewModalOpen] = useState(false);

  const [scrollIndex, setScrollIndex] = useState<number | undefined>(undefined);

  const scrollToTop = (refetch = false) => {
    setScrollIndex(0);
    setSelectAll(false);
    setSelectedCases([]);
    if (refetch) {
      clearCrmRequests();
      removeCrmRequests();
      refetchCrmRequests();
    }
  };

  const onQueryChange = useDebouncedCallback(
    (value: string) => {
      setDebouncedQuery(value);
      scrollToTop();
    },
    1000,
    query,
  );

  const {
    crmRequests,
    crmRequestsTotal,
    fetchMoreCrmRequests,
    isCrmRequestsLoading,
    isCrmRequestsFetching,
    crmRequestsError,
    clearCrmRequests,
    removeCrmRequests,
    refetchCrmRequests,
  } = useInfiniteCrmRequests({
    take: 100,
    query: debouncedQuery,
    filters: {
      advancedLogic: advancedLogic !== "undefined" ? advancedLogic : undefined,
      filters,
    },
    sort: sortBy,
  });
  const formattedError = useMemo(() => extractErrorInfo(crmRequestsError), [crmRequestsError]);

  function onSelectAll(checked: boolean) {
    setSelectAll(checked);
    if (checked) {
      const allIds = crmRequests.map((request) => request.caseId);
      setSelectedCases(allIds);
      return;
    }
    setSelectedCases([]);
  }

  const defaultColumnsOverwrites: Partial<
    Record<
      keyof (typeof crmRequests)[0],
      ReactTableColumn<(typeof crmRequests)[0]> & {
        flexGrow?: number;
        flexShrink?: number;
        disableSortBy?: boolean;
        sortDescFirst?: boolean;
        style?: React.CSSProperties;
      }
    >
  > = {
    caseId: {
      accessor: "caseId",
      Header: <Checkbox checked={selectAll} onClick={(_, { checked }) => onSelectAll(!!checked)} />,
      Cell: ({ cell }) => {
        return (
          <div className='tw-h-full tw-w-full' onClick={(e) => e.stopPropagation()} role='button' tabIndex={-1}>
            <Checkbox
              checked={selectedCases.includes(cell.value)}
              onClick={() => {
                if (selectedCases.includes(cell.value)) {
                  setSelectedCases(selectedCases.filter((caseId) => caseId !== cell.value));
                  return;
                }
                setSelectedCases([...selectedCases, cell.value]);
              }}
            />
          </div>
        );
      },
      width: 40,
      flexShrink: 0,
      disableSortBy: true,
    },
    displayId: {
      Header: "Request #",
      accessor: "displayId",
      flexShrink: 0,
      style: {
        fontSize: ".9em",
      },
      Cell: ({ cell }: any) => {
        const id = cell.row.original.caseId;
        const pageWhenDone = encodeURIComponent(window.location.href);
        const legacyUrl = `${AppSettings.GOGOV_WEB_URL}/${site}/editcase.php?cmd=edit&pagewhendone=${pageWhenDone}&id=${id}`;
        return (
          <Button
            icon
            size='mini'
            onClick={(e: any) => {
              e.stopPropagation();
              window.open(legacyUrl, "_blank");
            }}
          >
            {cell.value}
            <Icon name='external square alternate' size='large' />
          </Button>
        );
      },
    },
    thumbnail: {
      accessor: "thumbnail",
      Header: <Icon name='camera' className='tw-m-auto tw-w-full' />,
      Cell: (data) => {
        const { cell, row } = data;
        const { original } = row;
        const { hasAttachment, caseId } = original;
        const url = cell.value;
        if (!hasAttachment) {
          return <></>;
        }
        if (!url) {
          return <Icon name='file outline' size='huge' color='grey' className='tw-w-full tw-pt-[15px]' />;
        }
        const thumbnail = (
          <div
            className='tw-h-[90px] tw-w-[100px] tw-overflow-hidden'
            style={{
              background: `url(${url}) no-repeat center`,
              backgroundSize: "cover",
            }}
          />
        );
        const content = <img className='preview' alt={String(caseId)} src={url} />;
        return (
          <Popup
            pinned
            hoverable
            className='CrmListBody'
            trigger={thumbnail}
            content={content}
            position='right center'
          />
        );
      },
      width: 100,
      flexShrink: 0,
      style: {
        padding: 0,
        minWidth: "100px",
      },
      disableSortBy: true,
    },
    dateExpectClose: {
      Header: "Due Date",
      accessor: "dateExpectClose",
      Cell: (data) => {
        const dateClosed = data.row.original.dateClosed;
        if (dateClosed) {
          return <DateBadge value={data.cell.value} />;
        } else {
          return <DateDueBadge value={data.cell.value} />;
        }
      },
      width: 116,
      flexGrow: 0,
      flexShrink: 0,
      style: {
        textOverflow: "unset",
      },
    },
    dateLastUpdated: {
      Header: "Date Modified",
      accessor: "dateLastUpdated",
      Cell: ({ cell }) => <DateTimeBadge value={cell.value} />,
      width: 115,
      flexGrow: 0,
      flexShrink: 0,
      sortDescFirst: true,
      style: {
        textOverflow: "unset",
      },
    },
    dateEntered: {
      Header: "Date Entered",
      accessor: "dateEntered",
      Cell: ({ cell }) => <DateTimeBadge value={cell.value} />,
      width: 115,
    },
  };

  const tableColumns = useMemo(() => {
    return crmListFieldDefinitions
      ? ListViewDesktopColumns({
          columns,
          mainRecords: crmRequests,
          defaultFelidDefinitions: crmListFieldDefinitions.fields,
          customFieldDefinitions: crmListFieldDefinitions.customFields,
          overrideColumns: defaultColumnsOverwrites,
        })
      : [];
  }, [crmRequests, selectedCases, selectAll, columns]);

  const { rows, prepareRow, headers } = useTable(
    {
      columns: tableColumns,
      data: crmRequests,
      useControlledState: (state) => {
        return useMemo(
          () => ({
            ...state,
            sortBy: sortBy.map((sort) => ({
              id: sort.fieldName,
              desc: sort.direction === "desc",
            })),
          }),
          [state, sortBy],
        );
      },
      manualSortBy: true,
    },
    useSortBy,
    useColumnOrder,
  );

  const totalWidth = headers.reduce((acc, column) => {
    if (!column.isVisible) return acc;
    if (column.width) return Number(column.width) + acc;
    return column.minWidth ?? acc;
  }, 0);

  const toggleSortBy = (columnId: string, clearPrevious = false) => {
    setSortBy((prev) => {
      //TODO: support field id when custom fields are added
      //TODO: add clearing of sort when sorting by multiple columns is added
      const sortColumnIndex = tableColumns.findIndex((column) => column.accessor === columnId);
      if (sortColumnIndex === -1) return prev;
      const existingSort = prev.find((sort) => sort.fieldName === columnId);
      const fieldDefinition =
        crmListFieldDefinitions?.fields[columnId as keyof typeof crmListFieldDefinitions.fields] ||
        crmListFieldDefinitions?.customFields[columnId as keyof typeof crmListFieldDefinitions.customFields];

      if (!fieldDefinition) return prev;

      if (clearPrevious) {
        return [
          {
            fieldName: columnId,
            fieldId: "fieldId" in fieldDefinition ? fieldDefinition.fieldId : undefined,
            fieldType: fieldDefinition.type,
            direction: existingSort && existingSort.direction === "asc" ? "desc" : "asc",
            order: 1,
          },
        ];
      }
      if (existingSort)
        return prev.map((sort) => (sort.fieldName === columnId ? { ...sort, order: sortColumnIndex } : sort));
      return [
        ...prev,
        {
          fieldName: columnId,
          fieldId: "fieldId" in fieldDefinition ? fieldDefinition.fieldId : undefined,
          fieldType: fieldDefinition.type,
          direction: "asc",
          order: sortColumnIndex,
        },
      ];
    });
  };

  const viewListTrigger = (
    <span style={{ fontSize: isSmallScreen ? 18 : 24, fontWeight: "bold" }}>
      {currentView?.name || "Select View"}
      <Icon name='dropdown' />
    </span>
  );
  const viewOptionsTrigger = (
    <span>
      <Icon name='options' /> View Options
    </span>
  );
  const viewSection = (header: string, views: CrmCustomListView[]) => {
    return (
      <>
        <Dropdown.Header className='tw-mb-[4px] tw-tracking-[1px]' content={header} />
        {views.map((view: CrmCustomListView) => {
          return (
            <Dropdown.Item
              key={view.id}
              onClick={() => {
                setCurrentView(String(view.id));
              }}
              text={view.name}
              className={classNames({ "tw-bg-[#dcdfec]": String(view.id) === viewId, "tw-ml-[16px]": true })}
            />
          );
        })}
      </>
    );
  };

  return (
    <>
      <TopNavigation
        tabs={[
          { to: "/crm/list", label: "Requests", activeWhenURLIncludes: "/crm/list" },
          { to: "/crm/reports", label: "Reports", activeWhenURLIncludes: "/crm/reports" },
        ]}
      />
      {isOpenSaveNewViewModalOpen && (
        <SaveNewCrmListViewModal
          open={isOpenSaveNewViewModalOpen}
          onClose={() => {
            setIsOpenSaveNewViewModalOpen(false);
          }}
          onSubmit={(data) => {
            if (isNewView) {
              createView({
                ...data,
                filterGroup: {
                  advancedLogic,
                  filters,
                },
                sortFields: sortBy,
                query,
              });
            } else {
              if (!currentView?.id) {
                throw new Error("No Current View");
              }
              updateView(
                {
                  ...data,
                  filterGroup: {
                    advancedLogic,
                    filters,
                  },
                  sortFields: sortBy,
                  query,
                },
                Number(currentView.id),
              );
            }
            setIsOpenSaveNewViewModalOpen(false);
          }}
          departmentalAccess={departmentalAccess}
          fullAccess={fullAccess}
          isNew={isNewView}
          currentListView={currentView || undefined}
        />
      )}
      {isDeleteViewModalOpen && (
        <RemoveViewModal
          open={isDeleteViewModalOpen}
          close={() => {
            setIsDeleteViewModalOpen(false);
          }}
          onSubmit={async () => {
            if (!currentView) return;
            await deleteCustomListViewMutation(Number(currentView.id));
            setCurrentView("0");
            setIsDeleteViewModalOpen(false);
          }}
        />
      )}
      {isLoading && (
        <div className='tw-flex tw-h-screen tw-items-center tw-justify-center'>
          <Loader active inline='centered' size='large' />
        </div>
      )}
      {!isLoading && isFilterSideBarOpen && (
        <FilterFlyout
          open={isFilterSideBarOpen}
          onClose={closeFilterSidebar}
          fieldDefinitions={{
            ...crmListFieldDefinitions?.fields,
            ...crmListFieldDefinitions?.customFields,
          }}
          filterGroup={{
            advancedLogic: advancedLogic,
            filters,
          }}
          onSubmit={(data) => {
            if (data?.advancedLogic) {
              setAdvancedLogic(data.advancedLogic);
            } else {
              setAdvancedLogic("");
            }
            if (data?.filters) {
              setFilters(data.filters);
            }
            closeFilterSidebar();
            scrollToTop();
          }}
          onViewSave={async (data) => {
            if (data?.advancedLogic) {
              setAdvancedLogic(data.advancedLogic);
            } else {
              setAdvancedLogic("");
            }
            if (data?.filters) {
              setFilters(data.filters);
            }
            if (!currentView) return;
            updateView(
              {
                ...currentView,
                filterGroup: {
                  advancedLogic: data?.advancedLogic,
                  filters: data?.filters || [],
                },
              },
              Number(currentView.id),
            );
            closeFilterSidebar();
            scrollToTop();
          }}
          lookUpOptionsFunction={useCrmLookUpOptions}
          shouldRenderSaveToViewButton={!!currentView && !currentView.isReadOnly}
        />
      )}
      {!isLoading && isColumnSideBarOpen && (
        <ColumnFlyout
          open={isColumnSideBarOpen}
          onClose={closeColumnSidebar}
          columns={columns}
          fieldDefinitions={{
            ...crmListFieldDefinitions?.fields,
            ...crmListFieldDefinitions?.customFields,
          }}
          onSubmit={(data) => {
            setColumns(data);
            //TODO: update sortBy
            closeColumnSidebar();
          }}
          onViewSave={async (data) => {
            setColumns(data);
            if (currentView) {
              updateView(
                {
                  ...currentView,
                  columns: data,
                },
                Number(currentView.id),
              );
            }
            closeColumnSidebar();
          }}
          shouldRenderSaveToViewButton={!!currentView && !currentView.isReadOnly}
        />
      )}
      {!isLoading && !!crmCustomListViews && (
        <div className='tw-h-fit tw-p-1 sm:tw-p-2'>
          <Segment className='ui segment'>
            <div className='tw-mb-5 tw-flex tw-flex-col tw-items-start tw-justify-between sm:tw-flex-row sm:tw-items-center'>
              <Dropdown
                trigger={viewListTrigger}
                className='viewOptions indented-items "tw-mr-[16px]" tw-p-[8px]'
                scrolling
              >
                <Dropdown.Menu className='tw-max-h-[calc(100vh-130px)] tw-overflow-y-auto'>
                  {viewSection(
                    "MY VIEWS",
                    crmCustomListViews.filter((view) => view.accessLevel === "Personal"),
                  )}
                  {viewSection(
                    "GLOBAL VIEWS",
                    crmCustomListViews.filter((view) => view.accessLevel === "Global"),
                  )}
                  {Object.entries(
                    Object.groupBy(
                      crmCustomListViews.filter((view) => view.accessLevel === "Departmental" && view.departmentName),
                      (view) => view.departmentName!,
                    ),
                  ).map(([departmentName, views]) => (
                    <React.Fragment key={departmentName}>
                      {!!views && viewSection(departmentName, views)}
                    </React.Fragment>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <div className='tw-ml-auto tw-flex tw-space-x-0 sm:tw-space-x-1'>
                {canReturnToClassic && (
                  <Button
                    className='tw-m-0 tw-bg-transparent tw-p-2 tw-text-blue'
                    onClick={async () => {
                      await listModeCrmPreference.update("legacy");
                      await listModeCePreference.update("legacy");
                      window.location.href = `${AppSettings.GOGOV_WEB_URL}/${site}/internal.php`;
                    }}
                  >
                    Return to Classic
                  </Button>
                )}
                {landingPreference.value != "crm.list" && (
                  <Button
                    className='tw-m-0 tw-bg-transparent tw-p-2 tw-text-blue'
                    onClick={() => landingPreference.update("crm.list")}
                  >
                    Set as Default Page
                  </Button>
                )}
                {isCrmSearchExportLoading && <Loader active inline />}
                {!isCrmSearchExportLoading && (
                  <PlusButton
                    onClick={() => {
                      crmSearchExportMutation({
                        query: debouncedQuery,
                        filters: {
                          advancedLogic: advancedLogic !== "undefined" ? advancedLogic : undefined,
                          filters,
                        },
                        sort: sortBy,
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faDownload} className='tw-mr-2' />
                    Export
                  </PlusButton>
                )}
                {/*TODO: add reference url that conditionally checks for mobile*/}
                <div className='tw-pl-4'>
                  <Button as='a' href={legacyUrl} secondary className='tw-flex tw-items-center tw-justify-center'>
                    Create
                  </Button>
                </div>
              </div>
            </div>
            <span className='tw-mr-2 tw-flex tw-gap-2'>
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  onQueryChange(event.target.value);
                }}
                placeholder='Search requests'
                iconPosition='left'
                icon='search'
                className='stream-search'
              />
              <div className='tw-flex tw-items-center tw-justify-center'>
                <PlusButton onClick={openFilterSideBar}>
                  <FontAwesomeIcon icon={faFilter} className='tw-mr-[4px]' />
                  {`Filters` + (filters.length > 0 ? ` (${filters.length})` : "")}
                </PlusButton>
              </div>
              <div className='tw-flex tw-items-center tw-justify-center'>
                <PlusButton onClick={openColumnSideBar}>
                  <FontAwesomeIcon icon={faTableColumns} className='tw-mr-[4px]' />
                  Columns
                </PlusButton>
              </div>
              <div>
                <Dropdown
                  trigger={viewOptionsTrigger}
                  className='viewOptions tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-px-2'
                >
                  <Dropdown.Menu>
                    <Dropdown.Item
                      text='Save'
                      onClick={async () => {
                        if (!currentView) return;
                        await updateView(
                          {
                            ...currentView,
                            filterGroup: {
                              advancedLogic,
                              filters,
                            },
                            sortFields: sortBy,
                            query,
                          },
                          Number(currentView.id),
                        );
                      }}
                      disabled={!currentView || currentView.isReadOnly}
                    />
                    <Dropdown.Item
                      text='Save as New...'
                      onClick={() => {
                        setIsOpenSaveNewViewModalOpen(true);
                        setIsNewView(true);
                      }}
                    />
                    <Dropdown.Item
                      text='Edit...'
                      onClick={() => {
                        setIsOpenSaveNewViewModalOpen(true);
                        setIsNewView(false);
                      }}
                      disabled={!currentView || currentView.isReadOnly}
                    />
                    <Dropdown.Item
                      text='Delete'
                      onClick={() => {
                        if (!currentView) return;
                        setIsDeleteViewModalOpen(true);
                      }}
                      disabled={!currentView || currentView.isReadOnly}
                    />
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              {viewChanged && !!currentView && (
                <span>
                  <PlusButton
                    onClick={() => {
                      if (!currentView) return;
                      updateAllQueryParams(currentView);
                    }}
                  >
                    <Icon name='redo' />
                    Reset View
                  </PlusButton>
                </span>
              )}
            </span>
          </Segment>
          <Segment attached='top' className='subText tw-flex tw-flex-col tw-gap-3 tw-px-[11px] tw-py-2 md:tw-flex-row'>
            {isCrmRequestsLoading && <span>Loading...</span>}
            <span className='tw-flex tw-items-center tw-gap-2'>
              {!!selectedCases.length ? (
                <span>{selectedCases.length} Requests selected</span>
              ) : (
                <span>Found {crmRequestsTotal >= 10000 ? "Over 10000" : crmRequestsTotal} Requests</span>
              )}
              <Loader inline active={isCrmRequestsFetching} size='tiny' />
            </span>
            <span className='tw-ml-auto'>
              <button
                className='ui button icon tw-bg-transparent tw-p-1 tw-font-light tw-text-black'
                onClick={() => {
                  if (scrollIndex !== 0) scrollToTop(true);
                }}
              >
                <i aria-hidden='true' className='angle double up icon tw-pr-4'></i>
                Top
              </button>
            </span>
          </Segment>
          <div className='applications-table-container tw-h-[calc(100vh-503px)] sm:tw-h-[calc(10vh-324px)] md:tw-h-[calc(100vh-246px)]'>
            {isCrmRequestsLoading && (
              <div className='tw-flex tw-h-full tw-items-center tw-justify-center'>
                <Loader active size='large' />
              </div>
            )}
            {!!crmRequestsError && !isCrmRequestsLoading && (
              <div className='tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center'>
                <FormHeader
                  showDivider
                  spaceBetween={false}
                  title={formattedError.title}
                  color={"#880808"}
                  icon={<FontAwesomeIcon icon={faExclamationTriangle} size='sm' />}
                  subHeader={formattedError.subHeader}
                />
                <h5>{formattedError.message}</h5>
              </div>
            )}
            {!crmRequestsError && !isCrmRequestsLoading && (
              <Segment attached='bottom' className='VZenTable tw-h-full tw-overflow-x-auto tw-overflow-y-hidden tw-p-0'>
                <InfiniteLoader
                  isRowLoaded={({ index }) => !!rows[index]}
                  loadMoreRows={async () => fetchMoreCrmRequests()}
                  rowCount={crmRequestsTotal}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <AutoSizer>
                      {({ width, height }) => (
                        <Table
                          onScroll={() => {
                            setScrollIndex(undefined); //if we don't do this, the table will always scroll to the top
                          }}
                          scrollToIndex={scrollIndex}
                          headerHeight={30} //TODO: don't hard code this, replace with dynamically calculated value when on mobile ticket
                          rowCount={rows.length}
                          overscanRowCount={25}
                          rowHeight={rowHeight}
                          ref={registerChild}
                          width={Math.max(width - 2, totalWidth)} // Magic 2 to account for border
                          height={height}
                          onRowsRendered={onRowsRendered}
                          rowGetter={({ index }) => {
                            const row = rows[index];
                            prepareRow(row);
                            return row;
                          }}
                          style={{ minWidth: `${Math.max(width - 2, totalWidth)}px` }} // Magic 2 to account for border
                          onRowClick={({ rowData }) => {
                            const id = rowData.original.caseId;
                            const pageWhenDone = encodeURIComponent(window.location.href);
                            const legacyUrl = `${AppSettings.GOGOV_WEB_URL}/${site}/editcase.php?cmd=edit&pagewhendone=${pageWhenDone}&id=${id}`;
                            //TODO: add mobile url
                            window.location.href = legacyUrl;
                          }}
                        >
                          {headers
                            .filter((column) => column.isVisible)
                            .map((column) => (
                              <Column
                                key={column.id}
                                dataKey={column.id}
                                maxWidth={column.maxWidth}
                                minWidth={column.minWidth}
                                flexGrow={(column as any).flexGrow}
                                flexShrink={(column as any).flexShrink}
                                width={typeof column.width === "number" ? column.width : 150}
                                headerClassName={classNames({
                                  "tw-cursor-pointer": column.canSort,
                                })}
                                headerRenderer={() => (
                                  <div
                                    role='button'
                                    tabIndex={-1}
                                    onClick={() => {
                                      if (column.canSort) {
                                        toggleSortBy(column.id, true);
                                        scrollToTop();
                                      }
                                    }}
                                  >
                                    {column.render("Header")}
                                    {column.isSorted ? (
                                      column.isSortedDesc ? (
                                        <Icon name='caret down' />
                                      ) : (
                                        <Icon name='caret up' />
                                      )
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                )}
                                cellRenderer={({ rowIndex, columnIndex }) => {
                                  return rows[rowIndex].cells[columnIndex].render("Cell");
                                }}
                              />
                            ))}
                        </Table>
                      )}
                    </AutoSizer>
                  )}
                </InfiniteLoader>
              </Segment>
            )}
          </div>
        </div>
      )}
    </>
  );
};
